import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShieldCheck, Truck, Wallet } from 'lucide-react';
import AnnouncementBar from '../components/AnnouncementBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADDR_KEY = 'gk_saved_address';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const formRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);

  const shipping = subtotal >= 499 ? 0 : 49;
  const total = subtotal + shipping;

  // Restore saved address from localStorage (if any)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ADDR_KEY);
      if (raw) setSaved(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    if (items.length === 0 && !submitting) {
      // No items in cart — redirect back home after a beat
    }
  }, [items.length, submitting]);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast({ title: 'Cart is empty' }); return; }

    // Read values directly from the form — more reliable than controlled state
    const fd = new FormData(formRef.current);
    const address = {
      full_name: (fd.get('full_name') || '').toString().trim(),
      phone: (fd.get('phone') || '').toString().trim(),
      email: (fd.get('email') || '').toString().trim(),
      line1: (fd.get('line1') || '').toString().trim(),
      line2: (fd.get('line2') || '').toString().trim(),
      city: (fd.get('city') || '').toString().trim(),
      state: (fd.get('state') || '').toString().trim(),
      pincode: (fd.get('pincode') || '').toString().trim(),
    };
    const notes = (fd.get('notes') || '').toString().trim();

    const missing = ['full_name', 'phone', 'line1', 'city', 'state', 'pincode'].filter(k => !address[k]);
    if (missing.length) {
      toast({ title: 'Please complete the address', description: `Missing: ${missing.map(m => m.replace('_', ' ')).join(', ')}` });
      return;
    }
    if (!/^\d{6}$/.test(address.pincode)) { toast({ title: 'Enter a valid 6-digit pincode' }); return; }
    if (!/^\d{10}$/.test(address.phone.replace(/\D/g, ''))) { toast({ title: 'Enter a valid 10-digit phone number' }); return; }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map(i => ({ product_id: i.id, name: i.name, size: i.size, price: i.price, qty: i.qty, image: i.image })),
        address,
        payment_method: 'cod',
        notes,
      };
      const res = await axios.post(`${API}/orders`, payload, { withCredentials: true });
      try { localStorage.setItem(ADDR_KEY, JSON.stringify(address)); } catch {}
      clear();
      navigate(`/order/success/${res.data.order_id}`);
    } catch (err) {
      console.error('order failed', err);
      toast({ title: 'Could not place order', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AnnouncementBar />
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm mb-6" style={{ color: 'var(--gk-green-dark)' }}>
          <ArrowLeft size={16} /> Back to cart
        </button>
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-8" style={{ color: 'var(--gk-green-dark)' }}>Checkout</h1>

        <div className="grid md:grid-cols-3 gap-8">
          <form ref={formRef} onSubmit={submit} noValidate className="md:col-span-2 space-y-6">
            <section className="p-6 rounded-xl" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
              <h2 className="font-serif text-xl font-bold mb-4" style={{ color: 'var(--gk-green-dark)' }}>Shipping Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field name="full_name" label="Full name" required defaultValue={saved?.full_name || user?.name || ''} data-testid="checkout-fullname" />
                <Field name="phone" label="Phone" required placeholder="10-digit mobile" inputMode="numeric" defaultValue={saved?.phone || ''} data-testid="checkout-phone" />
                <Field name="email" label="Email" type="email" defaultValue={saved?.email || user?.email || ''} data-testid="checkout-email" className="md:col-span-2" />
                <Field name="line1" label="Address line 1" required placeholder="House / Flat, Street" defaultValue={saved?.line1 || ''} data-testid="checkout-line1" className="md:col-span-2" />
                <Field name="line2" label="Address line 2" placeholder="Landmark, Area (optional)" defaultValue={saved?.line2 || ''} data-testid="checkout-line2" className="md:col-span-2" />
                <Field name="city" label="City" required defaultValue={saved?.city || ''} data-testid="checkout-city" />
                <Field name="state" label="State" required defaultValue={saved?.state || ''} data-testid="checkout-state" />
                <Field name="pincode" label="Pincode" required placeholder="6-digit" inputMode="numeric" defaultValue={saved?.pincode || ''} data-testid="checkout-pincode" />
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest" style={{ color: 'var(--gk-muted)' }}>Order notes (optional)</label>
                <textarea name="notes" rows={2} placeholder="E.g. leave with security" data-testid="checkout-notes"
                  className="mt-1 w-full px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--gk-cream)', border: '1px solid #ecdfc7' }} />
              </div>
            </section>

            <section className="p-6 rounded-xl" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
              <h2 className="font-serif text-xl font-bold mb-4" style={{ color: 'var(--gk-green-dark)' }}>Payment</h2>
              <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer" style={{ background: 'var(--gk-cream)', border: '2px solid var(--gk-green)' }}>
                <input type="radio" name="payment" checked readOnly className="accent-[color:var(--gk-green)]" />
                <Wallet size={20} style={{ color: 'var(--gk-green)' }} />
                <div>
                  <div className="font-semibold text-sm" style={{ color: 'var(--gk-ink)' }}>Cash on Delivery</div>
                  <div className="text-xs" style={{ color: 'var(--gk-muted)' }}>Pay in cash when your order arrives.</div>
                </div>
              </label>
              <div className="mt-3 p-3 rounded-lg text-xs" style={{ background: 'rgba(201,169,97,0.12)', color: 'var(--gk-gold-dark)' }}>
                Razorpay online payment (UPI, cards, netbanking) coming soon.
              </div>
            </section>

            <button type="submit" disabled={submitting || items.length === 0} data-testid="place-order-btn"
              className="gk-btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Placing order...' : `Place Order · ₹${total}`}
            </button>
          </form>

          <aside className="p-6 rounded-xl h-fit md:sticky md:top-24" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
            <h2 className="font-serif text-xl font-bold mb-4" style={{ color: 'var(--gk-green-dark)' }}>Order Summary</h2>
            <div className="space-y-3 max-h-[300px] overflow-auto pr-1">
              {items.map(i => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="w-14 h-14 rounded object-cover" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold" style={{ color: 'var(--gk-ink)' }}>{i.name}</div>
                    <div className="text-xs" style={{ color: 'var(--gk-muted)' }}>{i.size} · Qty {i.qty}</div>
                  </div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--gk-green-dark)' }}>₹{i.qty * i.price}</div>
                </div>
              ))}
              {items.length === 0 && <div className="text-sm" style={{ color: 'var(--gk-muted)' }}>Your cart is empty.</div>}
            </div>

            <div className="mt-5 pt-4 border-t space-y-2 text-sm" style={{ borderColor: '#ecdfc7' }}>
              <div className="flex justify-between"><span style={{ color: 'var(--gk-muted)' }}>Subtotal</span><span>₹{subtotal}</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--gk-muted)' }}>Shipping</span><span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span></div>
              <div className="flex justify-between font-serif text-lg font-bold pt-2" style={{ color: 'var(--gk-green-dark)' }}>
                <span>Total</span><span>₹{total}</span>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: 'var(--gk-muted)' }}>
              <ShieldCheck size={14} style={{ color: 'var(--gk-green)' }} />
              <span>Secure checkout · Free returns within 7 days</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: 'var(--gk-muted)' }}>
              <Truck size={14} style={{ color: 'var(--gk-green)' }} />
              <span>Ships within 2 business days</span>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Field({ label, required, className = '', ...props }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-widest" style={{ color: 'var(--gk-muted)' }}>{label}{required && <span style={{ color: 'var(--gk-terracotta)' }}> *</span>}</label>
      <input {...props} className="mt-1 w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[color:var(--gk-gold)]/40"
        style={{ background: 'var(--gk-cream)', border: '1px solid #ecdfc7', color: 'var(--gk-ink)' }} />
    </div>
  );
}
